import React from "react";
import { ActionPlanLinkConfiguration, basicValidation, fieldValidation, PublicActionPlanLink, rng } from "../../../amorg-common";
import { createActionPlanLink, deleteActionPlanLink, shareActionPlanLink } from "../../../api/actionPlanClientApi";
import { Button } from "../../../designSystem/button/Button";
import { ButtonRow } from "../../../designSystem/button/ButtonRow";
import { ChipPanel } from "../../../designSystem/Chip";
import { CollapsiblePanel } from "../../../designSystem/collapsible/CollapsiblePanel";
import { CopyLinkButton } from '../../../designSystem/compositeComponents/CopyLinkButton';
import { Flex } from "../../../designSystem/compositeComponents/FlexHelpers";
import { InputMessage } from "../../../designSystem/InputMessage";
import { TextArea } from "../../../designSystem/inputs/TextArea";
import { TextInput } from "../../../designSystem/inputs/TextInput";
import { Label } from "../../../designSystem/Label";
import { Link } from "../../../designSystem/Link";
import { LoadingPageOverlay } from "../../../designSystem/LoadingPageOverlay";
import { Modal } from "../../../designSystem/Modal";
import { P } from "../../../designSystem/typography/Body";
import { appUrlBuilder } from '../../../utils/appUrlBuilder';
import { InfoChip } from "../../../designSystem/Chip";

type ActionPlanLinkEditorProps = {
    actionPlanId: string,
    link?: PublicActionPlanLink,
    siteId: string,
    linkContext:
        { type: 'actionPlan'; } |
        { type: 'actionPlanTopic', topicId: string; } |
        { type: 'actionPoint', actionPointId: string; };
    onUpdated: () => void,
};
export function ActionPlanLinkEditor (props: ActionPlanLinkEditorProps): JSX.Element
{
    const [loading, setLoading] = React.useState(false);
    const [showModal, setShowModal] = React.useState(false);

    let url = 'missing';
    if (props.link)
        url = appUrlBuilder.actionPlan.public(props.link.id, props.siteId);

    async function handleCreateLink ()
    {
        setLoading(true);

        const linkConfig: ActionPlanLinkConfiguration = {
            ...props.linkContext,
            writeable: false
        };

        const result = await createActionPlanLink(props.actionPlanId, rng.getRandomId(), linkConfig);

        if (result.isSuccess)
        {
            props.onUpdated();
        }
        else
        {
            console.log('Error creating link', result.errorMessage);
        }
        setLoading(false);
    }

    async function handleDeleteLink (id: string)
    {
        setLoading(true);

        const result = await deleteActionPlanLink(id);

        if (result.isSuccess)
        {
            props.onUpdated();
            setShowModal(false);
        }
        else
        {
            console.log('Error deleting link', result.errorMessage);
        }
        setLoading(false);
    }

    async function handleSendLink (message: string, emails: string[])
    {
        setLoading(true);
        const result = await shareActionPlanLink(props.link.id || '', emails, message)
        if(result.isSuccess)
        {
            setShowModal(false);
            props.onUpdated();
        }
        setLoading(false);
    }

    let typeText = 'handleplanen';
    if (props.linkContext.type === 'actionPlanTopic')
        typeText = 'handleplanens emne';
    else if (props.linkContext.type === 'actionPoint')
        typeText = 'handlepunktet';

    return (
        <>
            <LoadingPageOverlay loading={loading} />

            <Button icon='interface_actions_share'
                toolTipText="Rediger deling"
                variant={props.link ? 'text' : 'clean'}
                onClick={() => setShowModal(true)} />

            <Modal showModal={showModal}
                title={`Deling af ${typeText}`}
                onCancel={() => setShowModal(false)}
                content={
                    <LinkPanel
                        type={props.linkContext.type}
                        link={props.link ? { id: props.link.id, url } : null}
                        sentEmails={props.link?.recipients || []}
                        onCreateLink={handleCreateLink}
                        onSendLink={(message:string, recipients:string[]) => handleSendLink(message, recipients)}
                        onDeleteLink={handleDeleteLink}
                    />
                } />
        </>
    );
}



type LinkPanelProps = {
    type: 'actionPlan' | 'actionPlanTopic' | 'actionPoint',
    link: { id: string, url: string; } | null,
    sentEmails: string[],
    onCreateLink: () => void,
    onSendLink: (message: string, emails: string[]) => void,
    onDeleteLink: (id: string) => void;
};
function LinkPanel (props: LinkPanelProps): JSX.Element
{
    const [emailsText, setEmailsText] = React.useState('');
    const [emailsMessage, setEmailsMessage] = React.useState('');

    type EmailStates = 'valid' | 'invalid' | 'duplicate' | 'already-sent';

    const emailStrings = emailsText.split(',').join(' ').split(';').join(' ').split(' ');
    const emails: { email: string, status: EmailStates; }[] = [];
    for (const emailString of emailStrings)
    {
        if (emailString.trim() !== '')
        {
            const email = emailString.trim();
            if (!basicValidation.isValidEmail(email))
            {
                emails.push({ email, status: 'invalid' });
            }
            else if (emails.find(e => e.email.toLocaleLowerCase() === emailString.toLocaleLowerCase()))
                emails.push({ email, status: 'duplicate' });
            else if (props.sentEmails.find(e => e.toLocaleLowerCase() === emailString.toLocaleLowerCase()))
                emails.push({ email, status: 'already-sent' });
            else
                emails.push({ email, status: 'valid' });
        }
    }

    let overallStatus: EmailStates = 'valid';
    if (emails.some(e => e.status === 'invalid'))
        overallStatus = 'invalid';
    else if (emails.some(e => e.status === 'duplicate'))
        overallStatus = 'duplicate';
    else if (emails.some(e => e.status === 'already-sent'))
        overallStatus = 'already-sent';

    const validMessage = fieldValidation.actionPlanLink.message(emailsMessage);
    return (
        <Flex flexDirection='column' gap={32}>


            {!props.link &&
                <Flex flexDirection="column" gap={8}>
                    <Label>Link til deling</Label>
                    <P>Der er ikke oprettet et link.</P>
                    <div>
                        <Button variant="filled" text="Opret link" onClick={props.onCreateLink} />
                    </div>
                </Flex>
            }

            {props.link &&
                <>
                    <Flex flexDirection="column" gap={4}>
                        <Label>Link til deling</Label>
                        <div>
                            <Link href={props.link.url} target="_blank">{props.link.url}</Link>
                        </div>
                        <Flex>
                            
                        </Flex>

                        <Flex>
                            <CopyLinkButton url={props.link.url} />
                            <Button variant="destructive-text"
                                icon="interface_actions_delete" text="Slet link" onClick={() => props.onDeleteLink(props.link.id)}
                            />
                        </Flex>
                    </Flex>

                    {props.sentEmails.length > 0
                        ?  <div>
                                <Label></Label>
                                <CollapsiblePanel 
                                    openHeader={<Label>Linket er tidligere delt</Label>}
                                    closedOrDefaultHeader={<Label>Linket er tidligere delt</Label>}
                                    defaultOpen={props.sentEmails.length < 5}>

                                    <ChipPanel>
                                        {props.sentEmails.map(email =>
                                            <InfoChip key={email}>{email}</InfoChip>
                                        )}
                                    </ChipPanel>

                                </CollapsiblePanel>
                            </div>
                        :   <div>
                                Linket er ikke delt tidligere
                            </div>
                    }

                    <Flex flexDirection="column" gap={4}>
                        <Label>Del linket</Label>
                        <TextInput
                            placeholder="Indtast e-mail adresser"
                            value={emailsText}
                            onChange={(val) => setEmailsText(val)}
                        />
                        <ChipPanel>
                            {emails.map((email, index) =>
                                <InfoChip key={index} 
                                    trailingIcon={email.status === 'valid' ? 'status_ok' : 'status_error'}
                                    variant={email.status === 'valid' ? 'filled' : 'warning'}>
                                    {email.email}
                                </InfoChip>
                            )}
                        </ChipPanel>

                        <div style={{ minHeight: '12px' }}>
                            {overallStatus === 'invalid' &&
                                <P>Der er en eller flere ugyldige e-mail adresser.</P>
                            }
                            {overallStatus === 'duplicate' &&
                                <P>En eller flere email adresser optræder flere gange.</P>
                            }
                            {overallStatus === 'already-sent' &&
                                <P>Der er en eller flere e-mail adresser, der allerede har modtaget linket.</P>
                            }
                        </div>
                    </Flex>

                    <Flex flexDirection="column" gap={4}>
                        <Label>Besked til modtagerne (ikke påkrævet)</Label>
                        <TextArea
                            disabled={emails.filter(e => e.status==='valid').length === 0}
                            value={emailsMessage}
                            onChange={(val) => setEmailsMessage(val)}
                        />

                        {!validMessage &&
                            <InputMessage inErrorState={true}>
                                Må ikke overstige 1000 tegn ({emailsMessage.length})
                            </InputMessage>
                        }


                        <ButtonRow>
                            <Button disabled={!validMessage || emails.length === 0 ||  overallStatus !== 'valid'} variant="filled" text="Send" onClick={() => props.onSendLink(emailsMessage, emails.map(em => em.email))} />
                        </ButtonRow>
                    </Flex>
                </>
            }

        </Flex>

    );
}